import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, Overline } from "./Reveal";
import { Cpu, Cloud, Boxes, Radio, Workflow, ShieldCheck, RadioTower, LayoutGrid } from "lucide-react";

const tech = [
  { name: "Artificial Intelligence", icon: Cpu, img: "https://images.unsplash.com/photo-1639322537228-f710d846310a" },
  { name: "Cloud Computing", icon: Cloud, img: "https://images.pexels.com/photos/12939551/pexels-photo-12939551.jpeg" },
  { name: "Blockchain", icon: Boxes, img: "https://images.pexels.com/photos/1089438/pexels-photo-1089438.jpeg" },
  { name: "Internet of Things", icon: Radio, img: "https://images.unsplash.com/photo-1716191299980-a6e8827ba10b" },
  { name: "Automation", icon: Workflow, img: "https://images.pexels.com/photos/18419508/pexels-photo-18419508.jpeg" },
  { name: "Cybersecurity", icon: ShieldCheck, img: "https://images.pexels.com/photos/1089438/pexels-photo-1089438.jpeg" },
  { name: "Telecom Networks", icon: RadioTower, img: "https://images.pexels.com/photos/2352277/pexels-photo-2352277.jpeg" },
  { name: "Enterprise Platforms", icon: LayoutGrid, img: "https://images.pexels.com/photos/12939551/pexels-photo-12939551.jpeg" },
];

export const TechExpertise = () => {
  const [active, setActive] = useState(0);
  return (
    <section id="expertise" className="relative z-10 border-t border-white/10 bg-ink-950 py-24 md:py-32" data-testid="expertise-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal><Overline>Technology Expertise</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 max-w-2xl font-display text-3xl font-bold tracking-tighter text-white sm:text-4xl md:text-5xl">
            Deep fluency across the technologies shaping the enterprise.
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:gap-16">
          <ul className="flex flex-col">
            {tech.map((t, i) => {
              const Icon = t.icon;
              const isActive = active === i;
              return (
                <li key={t.name}>
                  <button
                    onMouseEnter={() => setActive(i)}
                    onFocus={() => setActive(i)}
                    data-testid={`expertise-item-${i}`}
                    className="group flex w-full items-center justify-between border-b border-white/10 py-6 text-left transition-colors duration-300"
                  >
                    <span className="flex items-center gap-4">
                      <Icon className={`h-6 w-6 transition-colors duration-300 ${isActive ? "text-brand" : "text-zinc-600"}`} strokeWidth={1.5} />
                      <span className={`font-display text-2xl font-semibold tracking-tight transition-colors duration-300 sm:text-3xl ${isActive ? "text-white" : "text-zinc-500"}`}>
                        {t.name}
                      </span>
                    </span>
                    <span className={`text-xs font-mono transition-colors duration-300 ${isActive ? "text-brand" : "text-zinc-700"}`}>
                      0{i + 1}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="relative hidden min-h-[420px] overflow-hidden rounded-2xl border border-white/10 lg:block">
            <AnimatePresence mode="wait">
              <motion.img
                key={active}
                src={tech[active].img}
                alt={tech[active].name}
                initial={{ opacity: 0, scale: 1.08 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-ink-950/40" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-ink-950 to-transparent p-8">
              <span className="text-xs font-semibold uppercase tracking-[0.28em] text-brand">Focus Area</span>
              <p className="mt-2 font-display text-2xl font-bold text-white">{tech[active].name}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
